/**
 * Custom Filter Drawer — BM_STORE
 *
 * Implements the "All Filters" drawer UI with:
 *  - Dynamic filters fetched from the custom-search API on page load
 *  - Static filters (Merken, Prijs, Beschikbaarheid) built from Shopify data
 *  - Sub-panel navigation within the drawer
 *  - Active filter pills + badge counters
 *  - "Show Results" → calls the products API and updates the product grid
 *  - "Reset All Filters" clears all state
 *
 * Initialised by [data-custom-filter-drawer] element injected by
 * snippets/custom-filter-drawer.liquid
 */

(function () {
  'use strict';

  if (customElements.get('custom-filter-drawer-root')) return;

  class CustomFilterDrawer extends HTMLElement {
    constructor() {
      super();

      // Config from data attributes (set in Liquid)
      this.apiBase  = this.dataset.apiBase || '';
      this.locale   = this.dataset.locale || 'nl';
      // Guard against malformed JSON (e.g. Shopify returning a null count in a
      // filtered context) so a parse error can't break the whole drawer.
      try {
        this.staticFiltersData = JSON.parse(this.dataset.staticFilters || '[]');
      } catch (err) {
        this.staticFiltersData = [];
      }

      // Native mode (search page): the drawer is driven entirely by Shopify's
      // Search & Discovery filters and applies results through native facet
      // URLs — no custom-search API calls are made.
      this.nativeMode = this.dataset.nativeMode === 'true';
      this.searchPath = window.location.pathname;

      // Derive categoryIdentifiers from collection rules (tag = equals conditions)
      // collection object is passed via window.__cfd_collection__ (set by {{ collection | json }})
      const collectionData = window.__cfd_collection__ || {};
      console.log(collectionData)
      const rules = collectionData.rules || [];
      console.log('rules', rules)
      this.categoryIdentifiers = rules
        .filter(r => r.column === 'tag' && r.relation === 'equals')
        .map(r => r.condition);
      if (this.categoryIdentifiers.length === 0) {
        // On collection pages use handle; on search pages use search terms
        const fallback = collectionData.handle || collectionData.terms || '';
        if (fallback) {
          this.categoryIdentifiers = [fallback];
        }
      }

      // Search page context
      this.isSearchPage = collectionData.context === 'search';
      this.searchTerms  = collectionData.terms || '';

      // Internal state
      this.dynamicFilters  = {};   // { key: { label, values: [{value,label,count}] } }
      this.selectedDynamic = {};   // { key: Set<value> }
      this.selectedStatic  = {};   // { param_name: Set<value> }  (Shopify URL params)
      // Last *applied* selection (committed when "Show Results" succeeds).
      // Used to discard uncommitted picks when the drawer is closed without applying.
      this._committedDynamic = {};
      this._committedStatic  = {};
      this.totalResults    = 0;
      this.currentPage     = 1;
      // Stored payloads for pagination re-use
      this.currentFiltersPayload = {};
      this.currentPricePayload   = null;
      this.currentTagsPayload    = [];
      this.currentBrandsPayload  = [];
      this.searchVendorTags      = [];   // vendors of rendered results, sent as `tags` on search

      this.currentSort           = null;  // active API sort value
      this.currentSortPayload    = null;  // saved for load-more reuse
      this._filtersReady         = false; // true once filters API has returned
      this._pendingSubFilter     = null;  // queued _openSubFilter call made before API returned
    }

    connectedCallback() {
      this._bindEvents();
      this._fetchFilters();
    }

    // ── Hydrate selectedStatic from current URL params ───────
    // (kept as a no-op — native Shopify filter URL params are not used)
    _readUrlFilters() {}

    // ── DOM helpers ──────────────────────────────────────────

    _el(selector) {
      return this.querySelector(selector);
    }

    _els(selector) {
      return this.querySelectorAll(selector);
    }

    _mainPanel() { return this._el('#cfd-panel-main'); }
    _subPanel()  { return this._el('#cfd-panel-sub');  }

    // ── Event binding ────────────────────────────────────────

    _bindEvents() {
      // Open drawer — triggered by buttons outside this element.
      // If the button has data-filter-shortcut, open directly to that sub-filter.
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.js-open-custom-filter-drawer');
        if (!btn) return;
        const shortcut = btn.dataset.filterShortcut;
        if (shortcut) {
          this._openDrawer();
          const filterDef = this.staticFiltersData.find(f => f.param_name === shortcut);
          if (filterDef) {
            this._openSubFilter(filterDef.param_name, filterDef.label, 'static', filterDef.type);
          }
        } else {
          this._openDrawer();
        }
      });

      // Close drawer
      this._els('.js-cfd-close').forEach(btn =>
        btn.addEventListener('click', () => this._closeDrawer())
      );

      // Overlay
      const overlay = document.querySelector('.js-cfd-overlay');
      if (overlay) overlay.addEventListener('click', () => this._closeDrawer());

      // Native mode: the zero-results "use fewer filters / clear all" link is
      // Shopify's empty-state link, injected into the AJAX-swapped grid. Its URL
      // can still carry filters, so route it through a full reset instead — that
      // clears the drawer selection and the URL together.
      if (this.nativeMode) {
        document.addEventListener('click', (e) => {
          const link = e.target.closest('.search-empty-state-caption a, .collection-empty a');
          if (!link) return;
          e.preventDefault();
          this._resetAllFilters();
        });
      }

      // Back to main panel
      const backBtn = this._el('.js-cfd-back');
      if (backBtn) backBtn.addEventListener('click', () => this._navigateToMain());

      // Show results
      this._els('.js-cfd-show-results').forEach(btn =>
        btn.addEventListener('click', () => this._applyFilters())
      );

      // Reset all
      this._els('.js-cfd-reset').forEach(btn =>
        btn.addEventListener('click', () => this._resetAllFilters())
      );

      // Intercept native Shopify sort.
      // facets.js listens to `input` events on the FacetsFilterForm element (bubbling).
      // We intercept BOTH `input` and `change` in capture phase so propagation is
      // stopped before it reaches the form's debounced handler.
      // • input  → stopImmediatePropagation only (value has already changed in DOM)
      // • change → do the actual sort + API call
      const _sortInterceptor = (e) => {
        const sortSelect = e.target.closest('[name="sort_by"]');
        if (!sortSelect) return;
        e.stopImmediatePropagation(); // prevent facets.js from navigating
        e.preventDefault();
        if (e.type === 'change') {
          // Native mode uses Shopify's own sort_by values verbatim; the custom
          // API path maps them to its own sort keys.
          this.currentSort = this.nativeMode
            ? sortSelect.value
            : this._mapSortValue(sortSelect.value);
          // Update the sort button label to reflect the selected option
          const sortBtn = document.querySelector('.collection-sort-button');
          if (sortBtn) {
            const selectedText = sortSelect.options[sortSelect.selectedIndex]?.text || '';
            const icon = sortBtn.querySelector('svg')?.cloneNode(true);
            sortBtn.textContent = selectedText;
            if (icon) sortBtn.appendChild(icon);
          }
          this._applyFilters();
        }
      };
      document.addEventListener('input',  _sortInterceptor, true); // block facets.js `input` listener
      document.addEventListener('change', _sortInterceptor, true); // block any change-based handlers
    }

    // ── Map Shopify sort_by value → custom API sort key ───────
    _mapSortValue(shopifySort) {
      const map = {
        'price-ascending':    'price_asc',
        'price-descending':   'price_desc',
        'title-ascending':    'title_asc',
        'title-descending':   'title_desc',
        'created-ascending':  'oldest',
        'created-descending': 'newest',
      };
      return map[shopifySort] || null;
    }

    // ── Collect vendors of the products rendered on the search page ──
    // Each `#product-grid` card carries `data-gtm-item-brand="{{ product.vendor }}"`.
    _collectRenderedVendors() {
      const grid = document.querySelector('#product-grid');
      if (!grid) return [];
      const vendors = new Set();
      grid.querySelectorAll('[data-gtm-item-brand]').forEach((el) => {
        const vendor = (el.getAttribute('data-gtm-item-brand') || '').trim();
        if (vendor) vendors.add(vendor);
      });
      return [...vendors];
    }

    // ── Fetch dynamic filters from API ───────────────────────

    async _fetchFilters() {
      // Native mode: build the drawer from the Shopify Search & Discovery
      // filters serialised into staticFiltersData — no API request.
      if (this.nativeMode) {
        this._initNativeFilters();
        return;
      }

      // On the search page, pad the request with the vendors of the products
      // already rendered on the page so the filters API can resolve results
      // even when q alone returns none. q = search term, tags = product vendors.
      // Captured once here so /search/products (apply + load-more) reuses the
      // same set — the grid is replaced on apply, so re-reading it would drift.
      this.searchVendorTags = this.isSearchPage ? this._collectRenderedVendors() : [];
      const data = this.isSearchPage
        ? await searchFiltersApi(this.searchTerms, this.locale, [], {}, null, this.searchVendorTags)
        : await customSearchFiltersApi(this.categoryIdentifiers, this.locale);
      if (data) {
        this.dynamicFilters = this._normalizeFilters(data);
        // Prefer the API's Merken (vendor) values over the ones baked in at
        // page load: fold them into the existing static "Merken" group.
        this._mergeApiVendorIntoStatic();
      }

      this._filtersReady = true;

      // If a sub-filter panel was opened while the API was still in-flight,
      // render its options now that the data is available.
      if (this._pendingSubFilter) {
        const { key, type, filterType, optionsEl } = this._pendingSubFilter;
        this._pendingSubFilter = null;
        optionsEl.innerHTML = '';
        if (type === 'dynamic') {
          this._renderDynamicOptions(key, optionsEl);
        } else {
          this._renderStaticOptions(key, filterType, optionsEl);
        }
      }

      this._renderFilterGroups();
    }

    // ── Native (Search & Discovery) mode ─────────────────────
    // Seed the selection from the native active values so the drawer
    // reflects the filters already applied in the URL, then render.
    _initNativeFilters() {
      // Diagnostic: log every filter coming from the Search & Discovery app.
      console.log('[CustomFilter] S&D filters (native mode):', this.staticFiltersData);

      // Seed the selection from the URL — the URL is the source of truth and,
      // unlike the serialised value lists (which Shopify shrinks or empties on
      // zero-result renders), it always carries every applied filter. Without
      // this, list-filter selections were lost after a zero-result reload while
      // price survived (it lives on the filter object), so removing one pill
      // silently dropped the others.
      const urlParams = new URLSearchParams(window.location.search);

      // Price params (…gte/…lte) are handled via the filter defs.
      const priceParams = new Set();
      this.staticFiltersData.forEach((filter) => {
        if (filter.type !== 'price_range') return;
        if (filter.min_param) priceParams.add(filter.min_param);
        if (filter.max_param) priceParams.add(filter.max_param);
        const min = filter.min_param ? urlParams.get(filter.min_param) : null;
        const max = filter.max_param ? urlParams.get(filter.max_param) : null;
        if (min != null || max != null) {
          this.selectedStatic[filter.param_name] = {
            min: min != null ? min : '',
            max: max != null ? max : '',
            type: 'price_range',
          };
        }
      });

      // List params: every remaining filter.* param becomes a selection — even
      // when its group is missing from the serialised data, so removal always
      // rebuilds the URL correctly. Store the serialised (entity-encoded) form
      // when we can match it, so option checkmarks line up.
      urlParams.forEach((value, key) => {
        if (!key.startsWith('filter.') || priceParams.has(key)) return;
        const def = this.staticFiltersData.find((f) => f.param_name === key);
        let stored = value;
        if (def && Array.isArray(def.values)) {
          const match = def.values.find(
            (v) => this._decodeEntities(String(v.value)) === value
          );
          if (match) stored = String(match.value);
        }
        if (!this.selectedStatic[key]) this.selectedStatic[key] = new Set();
        this.selectedStatic[key].add(stored);
      });

      // Seed the active sort from the URL so applying a filter doesn't drop it.
      const urlSort = urlParams.get('sort_by');
      if (urlSort) this.currentSort = urlSort;

      this.dynamicFilters = {};
      this._filtersReady = true;
      this._commitState(); // current URL state is the applied baseline

      // On a filtered render Shopify prunes the value lists and zeroes the
      // counts. Fetch the base search render once (q only, no filter params)
      // and swap in its full option lists + real counts.
      const hasUrlFilters = Array.from(urlParams.keys()).some((k) => k.startsWith('filter.'));
      // Shopify search renders never carry narrowed counts, so once a filter
      // is applied the only numbers we have (base-query counts) would mislead
      // — hide counts and show plain option labels instead.
      this._hideCounts = hasUrlFilters;
      if (hasUrlFilters) this._loadBaseNativeFilters();

      // If a sub-filter was opened before init completed, render it now.
      if (this._pendingSubFilter) {
        const { key, filterType, optionsEl } = this._pendingSubFilter;
        this._pendingSubFilter = null;
        optionsEl.innerHTML = '';
        this._renderStaticOptions(key, filterType, optionsEl);
      }

      this._renderFilterGroups();
    }

    // Fetch the unfiltered search render for the current query and swap in its
    // filter data — full value lists and real counts — so the drawer isn't
    // limited to the pruned/zeroed data of the filtered page render. Fails
    // soft: on any error the filtered-context data stays in place.
    async _loadBaseNativeFilters() {
      try {
        // Cache per query: filter changes reload the page with the same q, and
        // this fetch counts against Shopify's per-IP /search rate limit — only
        // the first filtered load for a query should pay the request.
        const cacheKey = `_cfdBaseFilters:${this.searchTerms}`;
        let fresh = null;
        try {
          fresh = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        } catch (e) { /* ignore bad cache */ }

        if (!Array.isArray(fresh) || !fresh.length) {
          const params = new URLSearchParams();
          if (this.searchTerms) {
            params.set('q', this.searchTerms);
            params.append('options[prefix]', 'last');
          }
          const sectionId = document.querySelector('#product-grid')?.dataset.id || 'main-search';
          const res = await fetch(`${this.searchPath}?${params.toString()}&section_id=${encodeURIComponent(sectionId)}`);
          if (!res.ok) return;

          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const root = doc.querySelector('custom-filter-drawer-root');
          if (!root) return;
          fresh = JSON.parse(root.getAttribute('data-static-filters') || '[]');
          if (!Array.isArray(fresh) || !fresh.length) return;
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(fresh));
          } catch (e) { /* storage full — proceed uncached */ }
        }

        // Use the base data wholesale. Shopify search renders never carry
        // narrowed counts (verified: identical counts under different applied
        // filters), so there is nothing useful to merge from the filtered
        // render — the base render's full lists are the best data available.
        this.staticFiltersData = fresh;

        // Selections seeded from the URL hold decoded values when their group
        // was pruned from the filtered render; re-map them to the serialised
        // (entity-encoded) form now that the full lists are back, so the
        // option checkmarks line up.
        Object.entries(this.selectedStatic).forEach(([key, val]) => {
          if (!(val instanceof Set)) return;
          const def = this.staticFiltersData.find((f) => f.param_name === key);
          if (!def || !Array.isArray(def.values)) return;
          const remapped = new Set();
          val.forEach((v) => {
            const match = def.values.find(
              (fv) => String(fv.value) === v ||
                      this._decodeEntities(String(fv.value)) === v
            );
            remapped.add(match ? String(match.value) : v);
          });
          this.selectedStatic[key] = remapped;
        });
        this._commitState(); // keep the applied baseline in the same form

        this._renderFilterGroups();
        this._updateGroupBadges();
      } catch (err) {
        console.error('[CustomFilter] base filter fetch failed:', err);
      }
    }

    /**
     * Normalise the API response to:
     *   { [key]: { label: string, values: [{value,label,count}] } }
     *
     * Supports common shapes:
     *   { filters: [...] }
     *   [ { key/id, label/name, values/options: [{value/id, label/name, count/productCount}] } ]
     */
    _normaliseFilters(data) {
      // alias — keep both spellings working
      return this._normalizeFilters(data);
    }

    _normalizeFilters(data) {
      const raw = Array.isArray(data)
        ? data
        : (data.data?.filters || data.data?.items || data.filters || data.items || data.result || []);
      const result = {};

      if (!Array.isArray(raw)) return result;

      raw.forEach((f) => {
        const key = f.key || f.id || f.code;
        if (!key) return;

        result[key] = {
          label: f.label || f.name || key,
          values: (f.values || f.options || []).map((v) => ({
            value: String(v.value),
            label: v.label || v.name || v.value,
            count: Number(v.count || 0),
          })),
        };
      });

      return result;
    }

    // ── Merge the API's Merken (vendor) filter into the static group ──
    // The static "Merken" group (filter.p.vendor) is serialised from Shopify
    // at page load. When /search/filters returns its own Merken filter we
    // prefer those values (they reflect the actual result set) and show them
    // inside the existing static group instead of as a separate dynamic one.
    _mergeApiVendorIntoStatic() {
      const staticVendor = this.staticFiltersData.find(
        (f) => f.param_name === 'filter.p.vendor'
      );
      if (!staticVendor) return;

      const vendorLabel = (staticVendor.label || '').trim().toLowerCase();
      const vendorKeys = ['vendor', 'brand', 'brands', 'merk', 'merken'];

      const matchKey = Object.keys(this.dynamicFilters).find((key) => {
        const f = this.dynamicFilters[key];
        const keyNorm = String(key).toLowerCase();
        const labelNorm = (f.label || '').trim().toLowerCase();
        return (
          (vendorLabel && labelNorm === vendorLabel) ||
          vendorKeys.some((k) => keyNorm.includes(k))
        );
      });
      if (!matchKey) return;

      // Show the API's Merken values in the static group…
      staticVendor.values = this.dynamicFilters[matchKey].values;
      // …and drop the API entry so it isn't rendered twice.
      delete this.dynamicFilters[matchKey];
    }

    // ── Render filter groups (main panel list) ────────────────

    _renderFilterGroups() {
      const groupsEl = this._el('.js-cfd-groups');
      if (!groupsEl) return;

      groupsEl.innerHTML = '';

      // 1. Static filters from Shopify (Merken, Prijs, Beschikbaarheid …)
      this.staticFiltersData.forEach((filter) => {
        const key = filter.param_name;
        const activeCount = filter.type === 'price_range'
          ? (this.selectedStatic[key] ? 1 : 0)
          : (this.selectedStatic[key] || new Set()).size;

        // Native mode: skip groups with nothing to show — every option has a
        // 0 count and none of them is selected (price is always shown).
        if (
          this.nativeMode &&
          filter.type !== 'price_range' &&
          activeCount === 0 &&
          !(filter.values || []).some((v) => Number(v.count) > 0)
        ) {
          return;
        }

        groupsEl.appendChild(
          this._createGroupItem(key, filter.label, activeCount, 'static', filter.type)
        );
      });

      // 2. If the filters API is still in-flight, dim static items and show
      //    a spinner so the user sees feedback when the drawer is opened early.
      if (!this._filtersReady) {
        groupsEl.classList.add('is-loading');
        const spinnerRow = document.createElement('li'); // changed from 'div'
        spinnerRow.className = 'cfd-groups-spinner';
        spinnerRow.innerHTML = '<div class="spinner-sm"></div>';
        groupsEl.appendChild(spinnerRow);
        return;
      }

      // Filters are ready — remove loading state.
      groupsEl.classList.remove('is-loading');

      // Dynamic filters from API (appended after static filters)
      Object.entries(this.dynamicFilters).forEach(([key, filter]) => {
        const activeCount = (this.selectedDynamic[key] || new Set()).size;
        groupsEl.appendChild(
          this._createGroupItem(key, filter.label, activeCount, 'dynamic')
        );
      });

      this._updateActivePills();
      this._syncFilterBarPills();
    }

    _createGroupItem(key, label, activeCount, type, filterType = 'list') {
      const item = document.createElement('li'); // changed from 'div'
      item.className = 'custom-filter-group-item';
      item.dataset.filterKey  = key;
      item.dataset.filterType = type;

      item.innerHTML = `
        <span class="custom-filter-group-label">${label}</span>
        ${activeCount > 0
          ? `<span class="custom-filter-group-badge">${activeCount}</span>`
          : ''}
        <svg class="custom-filter-group-arrow" width="8" height="14"
             viewBox="0 0 8 14" fill="none" aria-hidden="true">
          <path d="M1 1L7 7L1 13" stroke="currentColor"
                stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      item.addEventListener('click', () =>
        this._openSubFilter(key, label, type, filterType)
      );

      return item;
    }

    // ── Sub-filter panel ──────────────────────────────────────

    _openSubFilter(key, label, type, filterType) {
      const subPanel = this._subPanel();
      const titleEl  = this._el('.js-cfd-sub-title');
      const optionsEl = this._el('.js-cfd-options');

      if (!subPanel || !optionsEl) return;

      if (titleEl) titleEl.textContent = label.toUpperCase();

      this._mainPanel().classList.add('is-hidden');
      subPanel.classList.add('is-active');

      // For dynamic filters: if the filters API is still in-flight, show a
      // loading indicator and queue the render until the API completes.
      if (type === 'dynamic' && !this._filtersReady) {
        optionsEl.innerHTML = '<li class="custom-filter-loading">Filters laden…</li>';
        this._pendingSubFilter = { key, type, filterType, optionsEl };
        return;
      }

      // Otherwise render options immediately (static filters are always ready).
      optionsEl.innerHTML = '';
      if (type === 'dynamic') {
        this._renderDynamicOptions(key, optionsEl);
      } else {
        this._renderStaticOptions(key, filterType, optionsEl);
      }
    }

    _renderDynamicOptions(key, container) {
      const filter   = this.dynamicFilters[key];
      if (!filter) {
        container.innerHTML = '<li class="custom-filter-loading">Geen opties beschikbaar.</li>';
        return;
      }

      const selected = this.selectedDynamic[key] || new Set();

      // Sort: selected items first
      const values = [...filter.values].sort((a, b) => {
        const aS = selected.has(a.value) ? 0 : 1;
        const bS = selected.has(b.value) ? 0 : 1;
        return aS - bS;
      });

      values.forEach((v) => {
        container.appendChild(
          this._createOptionItem(key, v.value, v.label, v.count, selected.has(v.value))
        );
      });
    }

    _renderStaticOptions(key, filterType, container) {
      const filterDef = this.staticFiltersData.find((f) => f.param_name === key);
      if (!filterDef) return;

      if (filterType === 'price_range') {
        this._renderPriceRange(key, filterDef, container);
      } else {
        const selected = this.selectedStatic[key] || new Set();

        let values = [...(filterDef.values || [])];

        // Native mode: hide options with no results for this query — except
        // selected ones, which must stay visible so they can be unchecked.
        if (this.nativeMode) {
          values = values.filter(
            (v) => Number(v.count) > 0 || selected.has(v.value)
          );
        }

        // Sort: selected first
        values.sort((a, b) => {
          return (selected.has(a.value) ? 0 : 1) - (selected.has(b.value) ? 0 : 1);
        });

        values.forEach((v) => {
          const item = this._createOptionItem(
            key, v.value, v.label, v.count, selected.has(v.value), 'static'
          );
          container.appendChild(item);
        });
      }
    }

    _renderPriceRange(key, filterDef, container) {
      const current = this.selectedStatic[key] || {};
      const rangeMin = filterDef.range_min != null ? Number(filterDef.range_min) : 0;
      const rangeMax = filterDef.range_max != null ? Number(filterDef.range_max) : 10000;
      const currentMin = current.min !== undefined && current.min !== '' ? Number(current.min) : rangeMin;
      const currentMax = current.max !== undefined && current.max !== '' ? Number(current.max) : rangeMax;

      const wrapper = document.createElement('li'); // changed from 'div' so it's a valid <ul> child
      wrapper.className = 'custom-filter-price-range';
      wrapper.innerHTML = `
        <div class="custom-filter-price-range-fields">
          <input type="number" class="js-price-min"
                 value="${currentMin}" min="${rangeMin}" max="${rangeMax}" step="0.01">
          <span class="custom-filter-price-sep">tot</span>
          <input type="number" class="js-price-max"
                 value="${currentMax}" min="${rangeMin}" max="${rangeMax}" step="0.01">
        </div>
        <div class="cfd-price-slider-wrap">
          <div class="cfd-price-slider-track"></div>
          <div class="cfd-price-slider-range js-price-slider-range"></div>
          <input type="range" class="js-range-min"
                 min="${rangeMin}" max="${rangeMax}" value="${currentMin}" step="0.01">
          <input type="range" class="js-range-max"
                 min="${rangeMin}" max="${rangeMax}" value="${currentMax}" step="0.01">
        </div>
        <div class="cfd-vat-display" style="display: none">
          <p class="cfd-vat-title">VAT Display</p>
          <p class="cfd-vat-desc">All prices are immediately visible &ndash; Via the slider below there is the possibility to switch between including VAT or excluding VAT. The choice is automatically remembered when browsing Bouwmaat.nl and is always customizable.</p>
          <div class="cfd-vat-toggle-row">
            <label class="cfd-toggle">
              <input type="checkbox" class="js-vat-toggle">
              <span class="cfd-toggle-slider"></span>
            </label>
            <span class="cfd-vat-toggle-label">Prices excl. Show VAT</span>
          </div>
          <p class="cfd-vat-note js-vat-note">Prices excluding VAT are shown on the entire website.</p>
        </div>
      `;

      // Helper to update the blue fill track between the two thumbs
      const updateSliderRange = () => {
        const minVal = Number(wrapper.querySelector('.js-range-min').value);
        const maxVal = Number(wrapper.querySelector('.js-range-max').value);
        const span = rangeMax - rangeMin;
        const leftPct  = span > 0 ? ((minVal - rangeMin) / span) * 100 : 0;
        const rightPct = span > 0 ? ((rangeMax - maxVal) / span) * 100 : 0;
        const rangeEl = wrapper.querySelector('.js-price-slider-range');
        rangeEl.style.left  = leftPct + '%';
        rangeEl.style.right = rightPct + '%';
      };

      const storePrice = () => {
        const minVal = wrapper.querySelector('.js-price-min').value;
        const maxVal = wrapper.querySelector('.js-price-max').value;
        if (minVal !== '' || maxVal !== '') {
          this.selectedStatic[key] = { min: minVal, max: maxVal, type: 'price_range' };
        } else {
          delete this.selectedStatic[key];
        }
        this._updateActivePills();
        this._updateGroupBadges();
      };

      // Number inputs → sync sliders
      wrapper.querySelector('.js-price-min').addEventListener('change', (e) => {
        let val = Number(e.target.value);
        const maxVal = Number(wrapper.querySelector('.js-price-max').value);
        val = Math.min(Math.max(val, rangeMin), maxVal);
        e.target.value = val;
        wrapper.querySelector('.js-range-min').value = val;
        updateSliderRange();
        storePrice();
      });

      wrapper.querySelector('.js-price-max').addEventListener('change', (e) => {
        let val = Number(e.target.value);
        const minVal = Number(wrapper.querySelector('.js-price-min').value);
        val = Math.max(Math.min(val, rangeMax), minVal);
        e.target.value = val;
        wrapper.querySelector('.js-range-max').value = val;
        updateSliderRange();
        storePrice();
      });

      // Range sliders → sync number inputs
      wrapper.querySelector('.js-range-min').addEventListener('input', (e) => {
        let val = Number(e.target.value);
        const maxVal = Number(wrapper.querySelector('.js-range-max').value);
        if (val > maxVal) { val = maxVal; e.target.value = val; }
        wrapper.querySelector('.js-price-min').value = val;
        updateSliderRange();
        storePrice();
      });

      wrapper.querySelector('.js-range-max').addEventListener('input', (e) => {
        let val = Number(e.target.value);
        const minVal = Number(wrapper.querySelector('.js-range-min').value);
        if (val < minVal) { val = minVal; e.target.value = val; }
        wrapper.querySelector('.js-price-max').value = val;
        updateSliderRange();
        storePrice();
      });

      // VAT toggle
      wrapper.querySelector('.js-vat-toggle').addEventListener('change', (e) => {
        const note = wrapper.querySelector('.js-vat-note');
        if (e.target.checked) {
          note.textContent = 'Prices including VAT are shown on the entire website.';
        } else {
          note.textContent = 'Prices excluding VAT are shown on the entire website.';
        }
      });

      updateSliderRange();
      container.appendChild(wrapper);
    }

    _createOptionItem(filterKey, value, label, count, checked, type = 'dynamic') {
      const item = document.createElement('li'); // changed from 'div'
      item.className = `custom-filter-option-item ${checked ? 'is-checked' : ''}`;
      item.setAttribute('role', 'checkbox');
      item.setAttribute('aria-checked', checked ? 'true' : 'false');
      item.setAttribute('tabindex', '0');

      item.innerHTML = `
        <span class="custom-filter-option-text">${this._esc(label)}</span>
        ${this._hideCounts ? '' : `<span class="custom-filter-option-count">(${Number(count) || 0})</span>`}
        <span class="custom-filter-option-check-icon" aria-hidden="true">
          <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
            <path d="M1 5L4.5 8.5L11 1"
                  stroke="currentColor" stroke-width="1.5"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      `;

      const toggle = () => {
        const isChecked = item.classList.toggle('is-checked');
        item.setAttribute('aria-checked', isChecked ? 'true' : 'false');
        if (type === 'dynamic') {
          this._toggleDynamic(filterKey, value, isChecked);
        } else {
          this._toggleStatic(filterKey, value, isChecked);
        }
        this._updateActivePills();
        this._updateGroupBadges();
      };

      item.addEventListener('click', toggle);
      item.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); }
      });

      return item;
    }

    // called from toggle — defined here so _createOptionItem can reference it
    _onOptionToggle() {
      this._refreshFilters();
    }

    // ── Filter selection toggles ──────────────────────────────

    _toggleDynamic(key, value, checked) {
      if (!this.selectedDynamic[key]) this.selectedDynamic[key] = new Set();

      if (checked) {
        this.selectedDynamic[key].add(value);
      } else {
        this.selectedDynamic[key].delete(value);
        if (this.selectedDynamic[key].size === 0) delete this.selectedDynamic[key];
      }
    }

    _toggleStatic(key, value, checked) {
      if (!this.selectedStatic[key]) this.selectedStatic[key] = new Set();

      if (checked) {
        this.selectedStatic[key].add(value);
      } else {
        this.selectedStatic[key].delete(value);
        if (this.selectedStatic[key].size === 0) delete this.selectedStatic[key];
      }
    }

    // ── Active pills ──────────────────────────────────────────

    _updateActivePills() {
      const container = this._el('.js-cfd-active-pills');
      if (!container) return;

      container.innerHTML = '';

      // Dynamic filter pills
      Object.entries(this.selectedDynamic).forEach(([key, values]) => {
        const filter = this.dynamicFilters[key];
        values.forEach((val) => {
          const filterVal = filter?.values.find((v) => v.value === val);
          const label = filterVal?.label || val;
          container.appendChild(
            this._createActivePill(label, () => {
              this._toggleDynamic(key, val, false);
              this._updateActivePills();
              this._updateGroupBadges();
              this._renderFilterGroups();
            })
          );
        });
      });

      // Static filter pills
      Object.entries(this.selectedStatic).forEach(([key, valuesOrObj]) => {
        if (valuesOrObj && valuesOrObj.type === 'price_range') {
          const label = `€ ${valuesOrObj.min || '0'} – ${valuesOrObj.max || '∞'}`;
          container.appendChild(
            this._createActivePill(label, () => {
              delete this.selectedStatic[key];
              this._updateActivePills();
              this._updateGroupBadges();
            })
          );
        } else if (valuesOrObj instanceof Set) {
          const filterDef = this.staticFiltersData.find((f) => f.param_name === key);
          valuesOrObj.forEach((val) => {
            const valDef = filterDef?.values?.find((v) => v.value === val);
            const label = valDef?.label || val;
            container.appendChild(
              this._createActivePill(label, () => {
                this._toggleStatic(key, val, false);
                this._updateActivePills();
                this._updateGroupBadges();
              })
            );
          });
        }
      });

      container.style.display = container.children.length ? 'flex' : 'none';
    }

    _createActivePill(label, onRemove) {
      const pill = document.createElement('span');
      pill.className = 'custom-filter-active-pill';
      pill.innerHTML = `${this._esc(label)}
        <button class="custom-filter-pill-remove" type="button" aria-label="Filter verwijderen">
          &times;
        </button>`;
      pill.querySelector('.custom-filter-pill-remove').addEventListener('click', (e) => {
        e.stopPropagation();
        onRemove();
      });
      return pill;
    }

    // ── Group badges ──────────────────────────────────────────

    _updateGroupBadges() {
      this._els('.custom-filter-group-item').forEach((item) => {
        const key  = item.dataset.filterKey;
        const type = item.dataset.filterType;

        let count = 0;
        if (type === 'dynamic') {
          count = (this.selectedDynamic[key] || new Set()).size;
        } else {
          const val = this.selectedStatic[key];
          if (val && val.type === 'price_range') count = 1;
          else if (val instanceof Set) count = val.size;
        }

        let badge = item.querySelector('.custom-filter-group-badge');
        if (count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'custom-filter-group-badge';
            const arrow = item.querySelector('.custom-filter-group-arrow');
            item.insertBefore(badge, arrow);
          }
          badge.textContent = count;
        } else if (badge) {
          badge.remove();
        }
      });
    }

    // ── Navigation ────────────────────────────────────────────

    _navigateToMain() {
      this._mainPanel().classList.remove('is-hidden');
      this._subPanel().classList.remove('is-active');
      this._renderFilterGroups();
    }

    // ── Open / close ──────────────────────────────────────────

    _openDrawer() {
      this.classList.add('is-open');
      document.querySelector('.js-cfd-overlay')?.classList.add('is-visible');
      document.body.classList.add('filter-drawer-open');
      this._navigateToMain();
    }

    _closeDrawer() {
      this.classList.remove('is-open');
      document.querySelector('.js-cfd-overlay')?.classList.remove('is-visible');
      document.body.classList.remove('filter-drawer-open');

      // Closing without applying (overlay click / X / Esc) must discard any
      // selections the user made but did not confirm with "Show Results".
      // When the close is triggered from within _applyFilters, _isApplying is
      // true and the about-to-be-committed selection is kept.
      if (!this._isApplying) {
        this._restoreCommittedState();
      }
    }

    // ── Committed-selection snapshot (deep-cloned) ────────────
    // selectedDynamic / selectedStatic hold Sets and price-range objects, so a
    // shallow copy would alias the live state — clone each value defensively.
    _cloneSelection(selection) {
      const out = {};
      Object.entries(selection || {}).forEach(([key, value]) => {
        if (value instanceof Set) {
          out[key] = new Set(value);
        } else if (value && typeof value === 'object') {
          out[key] = { ...value };
        }
      });
      return out;
    }

    // Save the current selection as the applied baseline.
    _commitState() {
      this._committedDynamic = this._cloneSelection(this.selectedDynamic);
      this._committedStatic  = this._cloneSelection(this.selectedStatic);
    }

    // Roll the live selection back to the last applied baseline and re-render
    // every surface (drawer groups/pills + top PLP filter bar) so no orphaned
    // tags remain.
    _restoreCommittedState() {
      this.selectedDynamic = this._cloneSelection(this._committedDynamic);
      this.selectedStatic  = this._cloneSelection(this._committedStatic);
      this._renderFilterGroups();   // also refreshes active pills + bar pills
      this._updateActivePills();
      this._updateGroupBadges();
      this._syncFilterBarPills();
    }

    // ── Native apply (Search & Discovery facets) ─────────────

    // Build the native facet query string from the current selection.
    // List filters repeat their param_name; price uses the native
    // gte/lte param names; sort_by is Shopify's own value.
    _buildNativeParams() {
      const params = new URLSearchParams();

      if (this.searchTerms) {
        params.set('q', this.searchTerms);
        params.append('options[prefix]', 'last');
      }

      Object.entries(this.selectedStatic).forEach(([key, val]) => {
        if (val instanceof Set) {
          // Shopify returns filter `value.value` HTML-entity-encoded
          // (e.g. "Stucco d&#39;Or", "Alkyd &amp; Acrylkwast"). The native
          // theme decodes this implicitly via the checkbox's HTML attribute;
          // we build the URL in JS, so decode it here to match the real value.
          val.forEach((v) => params.append(key, this._decodeEntities(v)));
        } else if (val && val.type === 'price_range') {
          const def = this.staticFiltersData.find((f) => f.param_name === key);
          if (!def) return;
          if (val.min !== '' && val.min != null && def.min_param) {
            params.set(def.min_param, val.min);
          }
          if (val.max !== '' && val.max != null && def.max_param) {
            params.set(def.max_param, val.max);
          }
        }
      });

      if (this.currentSort) params.set('sort_by', this.currentSort);

      return params;
    }

    // Native mode applies via AJAX (the same Section Rendering API pattern as
    // Dawn's facets.js): fetch the re-rendered main-search section for the
    // facet URL and swap the grid in place. A full reload here would re-fire
    // the whole page-load request suite (mini-cart sections, /cart.js, stock,
    // beacons) on every filter change — rapid filtering then bursts the
    // store's per-IP rate limit (429s). AJAX costs one section fetch plus one
    // debounced stock call instead.
    async _applyNativeFilters() {
      this._setLoading(true);
      if (typeof showPageLoader === 'function') showPageLoader();
      let navigating = false;
      try {
        const params = this._buildNativeParams();
        const qs = params.toString();

        // Update the address bar immediately so pill changes reflect in the
        // URL without waiting on the network. Store searchParams so the native
        // FacetFiltersForm popstate handler can restore on back/forward.
        history.pushState({ searchParams: qs }, '', `${this.searchPath}?${qs}`);

        // Same fetch mechanism as native facets.js (?section_id=…, raw HTML).
        // Routed through CartQueue when available so a transient 429 gets the
        // same retry/backoff every other request in this theme has.
        const sectionId = document.querySelector('#product-grid')?.dataset.id || 'main-search';
        const _fetch = (window.CartQueue ? window.CartQueue.fetch : fetch);
        const res = await _fetch(`${this.searchPath}?${qs}&section_id=${encodeURIComponent(sectionId)}`);

        // Still rate-limited after retries: fall back to a full navigation.
        // A document request can pass the bot-protection challenge page
        // (background fetches never can), so the filter still applies instead
        // of the grid silently not updating. Loader stays up until unload.
        if (res.status === 429) {
          navigating = true;
          window.location.href = `${this.searchPath}?${qs}`;
          return;
        }
        if (!res.ok) throw new Error(`Section fetch failed: ${res.status}`);

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Product grid (already filtered + sorted by Shopify). At zero results
        // this carries the empty state; its "clear all" link is intercepted by
        // the native-mode click handler and routed through _resetAllFilters.
        const newGrid = doc.querySelector('#ProductGridContainer');
        const gridContainer = document.querySelector('#ProductGridContainer');
        if (newGrid && gridContainer) gridContainer.innerHTML = newGrid.innerHTML;

        // Product count (bar + heading)
        const newCount = doc.querySelector('[id="ProductCount"]');
        if (newCount) {
          document.querySelectorAll('[id="ProductCount"]').forEach((el) => {
            el.innerHTML = newCount.innerHTML;
            el.classList.remove('loading');
          });
        }

        // Drawer counts are base-query numbers; once any filter is active
        // they would mislead, so hide them (a reset reloads the page and
        // brings them back).
        this._hideCounts = true;

        this._commitState();      // this selection is now the applied baseline
        this._syncFilterBarPills();
        this._renderFilterGroups();
        this._closeDrawer();

        // Populate stock for the new cards BEFORE closing the loader, so cards
        // never show placeholder stock states after the loader hides. Safe to
        // await: inventoryAPI resolves in bounded time in every failure mode
        // (CartQueue retries are capped, then _stockApiRequest returns []).
        if (typeof inventoryAPI === 'function') {
          try {
            await inventoryAPI();
          } catch (err) {
            // Non-fatal: cards keep placeholder stock states.
            console.error('[CustomFilter] stock refresh after apply failed:', err);
          }
        }
      } finally {
        if (!navigating) {
          this._setLoading(false);
          if (typeof hidePageLoader === 'function') hidePageLoader();
        }
      }
    }

    // ── Apply filters → call products API ────────────────────

    async _applyFilters() {
      // If an apply is already running, remember that another was requested and
      // re-run once it finishes — otherwise fast successive changes (e.g.
      // removing several bar pills) would leave the URL/results out of sync with
      // the latest selection.
      if (this._isApplying) { this._applyPending = true; return; }
      this._isApplying = true;

      const hasAnyDynamic = Object.keys(this.selectedDynamic).length > 0;
      const hasAnyStatic  = Object.keys(this.selectedStatic).length > 0;
      const hasSort       = !!this.currentSort;

      if (!hasAnyDynamic && !hasAnyStatic && !hasSort) {
        this._isApplying = false;
        this._resetAllFilters();
        return;
      }

      // Native mode: apply through Shopify's Search & Discovery facets.
      if (this.nativeMode) {
        try {
          await this._applyNativeFilters();
        } catch (err) {
          console.error('[CustomFilter] native apply failed:', err);
        } finally {
          this._isApplying = false;
          if (this._applyPending) {
            this._applyPending = false;
            this._applyFilters();
          }
        }
        return;
      }

      this._setLoading(true);
      this.currentPage = 1; // Always start from page 1 on a new filter apply

      try {
        // Build filters object for API
        const filtersPayload = {};
        Object.entries(this.selectedDynamic).forEach(([key, values]) => {
          filtersPayload[key] = Array.from(values);
        });

        // Static list filters: vendor (MERKEN) → brandsPayload; other tags → tagsPayload; price_range → pricePayload
        const tagsPayload = [];
        const brandsPayload = [];
        let pricePayload = null;
        Object.entries(this.selectedStatic).forEach(([key, valuesOrObj]) => {
          if (valuesOrObj instanceof Set) {
            if (key === 'filter.p.vendor') {
              valuesOrObj.forEach((v) => brandsPayload.push(v));
            } else {
              valuesOrObj.forEach((v) => tagsPayload.push(v));
            }
          } else if (valuesOrObj && valuesOrObj.type === 'price_range') {
            const minNum = valuesOrObj.min !== '' ? Number(valuesOrObj.min) : undefined;
            const maxNum = valuesOrObj.max !== '' ? Number(valuesOrObj.max) : undefined;
            if (minNum !== undefined || maxNum !== undefined) {
              pricePayload = {};
              if (minNum !== undefined) pricePayload.min = minNum;
              if (maxNum !== undefined) pricePayload.max = maxNum;
            }
          }
        });

        // Store payloads so pagination can reuse them
        this.currentFiltersPayload = filtersPayload;
        this.currentPricePayload   = pricePayload;
        this.currentTagsPayload    = tagsPayload;
        this.currentBrandsPayload  = brandsPayload;
        this.currentSortPayload    = this.currentSort;

        const data = this.isSearchPage
          ? await searchProductsApi(this.searchTerms, brandsPayload, filtersPayload, pricePayload, this.currentPage, 20, this.currentSort, this.searchVendorTags)
          : await customSearchProductsApi(this.categoryIdentifiers, tagsPayload, brandsPayload, filtersPayload, pricePayload, this.currentPage, 20, this.currentSort);
        if (!data) throw new Error('Products API returned no data');

        const inner = data.data || data;
        const pagination = inner.pagination || {};
        this.totalResults = pagination.total || inner.total || inner.totalCount || inner.count || 0;

        await this._updateProductGrid(data);
        this._renderLoadMore(pagination);
        this._syncFilterBarPills();
        this._commitState(); // selection is now the applied baseline
        this._closeDrawer();
        this._refreshFilters(); // background: update filter counts after results shown

      } catch (err) {
        console.error('[CustomFilter] applyFilters failed:', err);
      } finally {
        this._isApplying = false;
        this._setLoading(false);
        if (this._applyPending) {
          this._applyPending = false;
          this._applyFilters();
        }
      }
    }

    /**
     * Update the product grid based on API response.
     *
     * The products API is expected to return at minimum an array of product
     * handles so we can match against the rendered cards.  Full card re-render
     * via Shopify section rendering can be layered on top later.
     *
     * Expected shapes (any of):
     *   { products: [ { handle }, … ], total: N }
     *   { items: [ { handle }, … ], total: N }
     *   [ { handle }, … ]
     */
    async _updateProductGrid(data) {
      // Support: { data: { products: [...] } } or { products: [...] } or [...]
      const inner = data.data || data;
      const rawProducts = Array.isArray(inner)
        ? inner
        : (inner.products || inner.items || []);

      const productGrid = document.querySelector('#product-grid');
      const gridContainer = document.querySelector('#ProductGridContainer');

      if (!gridContainer) return;

      if (rawProducts.length === 0) {
        gridContainer.innerHTML = `
          <div class="collection collection-empty" id="product-grid">
            <h2>Geen producten gevonden</h2>
            <p>Probeer minder filters te gebruiken of
               <a href="${window.location.pathname}" class="button-text-link">alle filters te wissen</a>.</p>
          </div>
        `;
        this._showResultsBar(0);
        return;
      }

      // Use Shopify's Section Rendering API — fetch the main-search section
      // with the returned product IDs so cards are rendered by card-product.liquid.
      const ids = rawProducts.map(p => p.shopifyProductId).filter(Boolean);
      if (ids.length > 0) {
        try {
          const query = ids.map(id => `id:${id}`).join(' OR ');
          const url = `/search?type=product&q=${encodeURIComponent(query)}&sections=main-search`;
          const response = await fetch(url);
          if (response.ok) {
            const json = await response.json();
            const sectionHtml = json['main-search'] || '';
            if (sectionHtml) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionHtml, 'text/html');
              const renderedGrid = doc.querySelector('#product-grid');
              if (renderedGrid) {
                // Build a map of product ID → rendered grid item so we can
                // re-order cards to match the API's sort order.
                const renderedItems = Array.from(renderedGrid.querySelectorAll(':scope > .grid-item'));
                const itemMap = new Map();
                renderedItems.forEach(li => {
                  const card = li.querySelector('product-card[data-product-id]');
                  if (card) itemMap.set(String(card.dataset.productId), li);
                });

                // Build ordered list matching API response order
                const orderedItems = [];
                ids.forEach(id => {
                  const li = itemMap.get(String(id));
                  if (li) orderedItems.push(li);
                });
                // Safety net: append any rendered items not in the ids list
                renderedItems.forEach(li => {
                  if (!orderedItems.includes(li)) orderedItems.push(li);
                });

                // Replace the entire gridContainer content so any stale
                // empty-state div is always removed before injecting new cards.
                gridContainer.innerHTML = '';
                const wrapper = doc.querySelector('.collection') || renderedGrid.parentElement;
                if (wrapper && wrapper !== doc.body) {
                  const wrapperClone = wrapper.cloneNode(true);
                  const gridInClone = wrapperClone.querySelector('#product-grid');
                  if (gridInClone) {
                    while (gridInClone.firstChild) gridInClone.removeChild(gridInClone.firstChild);
                    orderedItems.forEach(li => gridInClone.appendChild(li.cloneNode(true)));
                  }
                  gridContainer.appendChild(wrapperClone);
                } else {
                  const ul = document.createElement('ul');
                  ul.id = 'product-grid';
                  ul.className = renderedGrid.className;
                  orderedItems.forEach(li => ul.appendChild(li.cloneNode(true)));
                  gridContainer.appendChild(ul);
                }
                this._showResultsBar(this.totalResults || rawProducts.length);
                this._runInventoryWithLoader();
                return;
              }
            }
          }
        } catch (err) {
          console.error('[CustomFilter] Section rendering failed, using fallback:', err);
        }
      }

      // Fallback: rebuild proper grid structure so empty-state div is never reused.
      gridContainer.innerHTML = `
        <div class="collection">
          <div class="loading-overlay"></div>
          <ul id="product-grid" class="product-grid grid grid-2-col grid-3-col-tablet grid-4-col-desktop"></ul>
        </div>
      `;
      const newGrid = gridContainer.querySelector('#product-grid');
      rawProducts.forEach(product => newGrid.appendChild(this._buildProductCard(product)));
      this._showResultsBar(this.totalResults || rawProducts.length);
      this._runInventoryWithLoader();
    }

    async _runInventoryWithLoader() {
      if (typeof inventoryAPI !== 'function') return;
      if (typeof showPageLoader === 'function') showPageLoader();
      try {
        await inventoryAPI();
      } finally {
        if (typeof hidePageLoader === 'function') hidePageLoader();
      }
    }

    _buildProductCard(product) {
      const li = document.createElement('li');
      li.className = 'grid-item';
      li.dataset.productHandle = product.handle || '';

      const url    = product.productUrl || (product.handle ? `/products/${product.handle}` : '#');
      const imgSrc = product.image?.src || '';
      const imgAlt = this._esc(product.image?.alt || product.title || '');
      const title  = this._esc(product.title || '');
      const vendor = this._esc(product.vendor || '');
      const sku    = this._esc(product.variants?.[0]?.sku || '');
      const price  = product.minPrice != null ? `€ ${Number(product.minPrice).toFixed(2)}` : '';
      const comparePrice = product.variants?.[0]?.compareAtPrice
        ? `€ ${Number(product.variants[0].compareAtPrice).toFixed(2)}`
        : '';

      li.innerHTML = `
        <div class="card-desktop" data-product-handle="${this._esc(product.handle || '')}">
          <product-card class="card card-product" data-product-id="${this._esc(product.shopifyProductId || '')}">
            <div class="card-product-wishlist-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" stroke-width="1.5"
                   stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06
                         a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78
                         1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </div>
            <figure class="card-media card-media-contain">
              <a href="${url}" class="js-product-link" aria-label="${this._esc(product.title || '')}">
                <div class="aspect-ratio" style="--ratio-percent: 100%;">
                  ${imgSrc ? `<img src="${imgSrc}" alt="${imgAlt}" loading="lazy" class="card-media-image">` : ''}
                </div>
              </a>
            </figure>
            <div class="card-info">
              <div class="card-eyebrow">
                ${vendor ? `<span class="card-eyebrow-item">${vendor}</span>` : ''}
                ${sku ? `<span class="card-eyebrow-item" data-card-sku>${sku}</span>` : ''}
              </div>
              <h2 class="card-heading">
                <a class="js-product-link" href="${url}">${title}</a>
              </h2>
              <div class="card-stock-energy-label">
                <div class="availability-wrapper product-stock-availablity-card" data-productsku="${this._esc(sku)}">
                  <div class="availability-item no-stock"><span class="dot"></span><span class="label">Online</span></div>
                  <div class="availability-item no-stock"><span class="dot"></span><span class="label">In store</span></div>
                </div>
              </div>
            </div>
            <div class="card-footer">
              <div class="price-bulk-wrapper">
                <div class="price">
                  ${comparePrice
                    ? `<dl class="price price--on-sale">
                         <dt class="visually-hidden">Was</dt><dd class="price__compare"><s>${comparePrice}</s></dd>
                         <dt class="visually-hidden">Nu</dt><dd class="price__sale">${price}</dd>
                       </dl>`
                    : `<dl class="price"><dt class="visually-hidden">Prijs</dt><dd class="price__regular">${price}</dd></dl>`}
                </div>
              </div>
            </div>
          </product-card>
        </div>
      `;

      return li;
    }

    _showResultsBar(count) {
      let bar = document.querySelector('.custom-filter-results-bar');
      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'custom-filter-results-bar';
        const mainContent = document.querySelector('.page-layout-main-content');
        const gridContainer = document.querySelector('#ProductGridContainer');
        if (mainContent && gridContainer) {
          mainContent.insertBefore(bar, gridContainer);
        }
      }

      const activeFilterLabels = [
        ...Object.entries(this.selectedDynamic).flatMap(([key, values]) => {
          const filter = this.dynamicFilters[key];
          return Array.from(values).map((v) => {
            const def = filter?.values.find((fv) => fv.value === v);
            return def?.label || v;
          });
        }),
      ];

      bar.innerHTML = `
        <span><strong>${count}</strong> resultaten
          ${activeFilterLabels.length
            ? `voor <em>${activeFilterLabels.join(', ')}</em>`
            : ''}
        </span>
      `;

      // Sync all #ProductCount elements (collection actions bar + search heading)
      // with the filtered count. Save original HTML on first call so a page
      // reload (triggered by _resetAllFilters) naturally restores the value.
      document.querySelectorAll('[id="ProductCount"]').forEach(el => {
        if (!el.dataset.cfdOriginalHtml) {
          el.dataset.cfdOriginalHtml = el.innerHTML;
        }
        // Replace the first number in the original markup with the new count
        el.innerHTML = el.dataset.cfdOriginalHtml.replace(/\d[\d.,]*/, String(count));
      });
    }

    // ── Reset ─────────────────────────────────────────────────

    _resetAllFilters() {
      this.selectedDynamic = {};
      this.selectedStatic  = {};
      this._committedDynamic = {};
      this._committedStatic  = {};
      this.totalResults    = 0;
      this.currentSort     = null;

      // Remove results bar and load-more
      document.querySelector('.custom-filter-results-bar')?.remove();
      document.querySelector('.cfd-load-more-wrap')?.remove();

      this._renderFilterGroups();
      this._updateActivePills();
      this._syncFilterBarPills();

      // Clear Shopify native URL filters and reload to restore the original product list
      const url = new URL(window.location.href);
      const toDelete = [];
      url.searchParams.forEach((_, k) => {
        if (k.startsWith('filter.') || k === 'sort_by') toDelete.push(k);
      });
      toDelete.forEach((k) => url.searchParams.delete(k));

      // Always reload so that the original Shopify-rendered product grid is restored
      window.location.href = url.toString();
    }

    // ── Background filter refresh ──────────────────────────────

    _refreshFilters() {
      clearTimeout(this._refreshFiltersTimer);
      this._refreshFiltersTimer = setTimeout(() => this._doRefreshFilters(), 300);
    }

    async _doRefreshFilters() {
      // Build all payloads directly from live selection state
      const selectedFilters = {};
      Object.entries(this.selectedDynamic).forEach(([key, values]) => {
        selectedFilters[key] = Array.from(values);
      });

      const tagsPayload = [];
      const brandsPayload = [];
      let pricePayload = null;
      Object.entries(this.selectedStatic).forEach(([key, valuesOrObj]) => {
        if (valuesOrObj instanceof Set) {
          if (key === 'filter.p.vendor') {
            valuesOrObj.forEach(v => brandsPayload.push(v));
          } else {
            valuesOrObj.forEach(v => tagsPayload.push(v));
          }
        } else if (valuesOrObj && valuesOrObj.type === 'price_range') {
          const minNum = valuesOrObj.min !== '' ? Number(valuesOrObj.min) : undefined;
          const maxNum = valuesOrObj.max !== '' ? Number(valuesOrObj.max) : undefined;
          if (minNum !== undefined || maxNum !== undefined) {
            pricePayload = {};
            if (minNum !== undefined) pricePayload.min = minNum;
            if (maxNum !== undefined) pricePayload.max = maxNum;
          }
        }
      });

      try {
        const data = this.isSearchPage
          ? await searchFiltersApi(this.searchTerms, this.locale, brandsPayload, selectedFilters, pricePayload, this.searchVendorTags)
          : await customSearchFiltersApi(this.categoryIdentifiers, this.locale, tagsPayload, brandsPayload, selectedFilters, pricePayload);

        if (data) {
          this.dynamicFilters = this._normalizeFilters(data);
          // Keep the API's Merken values in the static group after selection too.
          this._mergeApiVendorIntoStatic();
          this._renderFilterGroups();
        }
      } catch (err) {
        console.error('[CustomFilter] refreshFilters failed:', err);
      }
    }

    // ── Load More ─────────────────────────────────────────────

    async _loadMore() {
      this.currentPage += 1;
      this._setLoadMoreLoading(true);
      try {
        const data = this.isSearchPage
          ? await searchProductsApi(this.searchTerms, this.currentBrandsPayload, this.currentFiltersPayload, this.currentPricePayload, this.currentPage, 20, this.currentSortPayload, this.searchVendorTags)
          : await customSearchProductsApi(this.categoryIdentifiers, this.currentTagsPayload, this.currentBrandsPayload, this.currentFiltersPayload, this.currentPricePayload, this.currentPage, 20, this.currentSortPayload);
        if (!data) return;
        const inner = data.data || data;
        const pagination = inner.pagination || {};
        this.totalResults = pagination.total || inner.total || 0;
        await this._appendProductsToGrid(data);
        this._renderLoadMore(pagination);
        this._showResultsBar(this.totalResults);
      } catch (err) {
        console.error('[CustomFilter] loadMore failed:', err);
        this.currentPage -= 1; // revert on error
      } finally {
        this._setLoadMoreLoading(false);
      }
    }

    async _appendProductsToGrid(data) {
      const inner = data.data || data;
      const rawProducts = Array.isArray(inner) ? inner : (inner.products || inner.items || []);
      if (!rawProducts.length) return;

      const ids = rawProducts.map(p => p.shopifyProductId).filter(Boolean);
      if (ids.length > 0) {
        try {
          const query = ids.map(id => `id:${id}`).join(' OR ');
          const url = `/search?type=product&q=${encodeURIComponent(query)}&sections=main-search`;
          const response = await fetch(url);
          if (response.ok) {
            const json = await response.json();
            const sectionHtml = json['main-search'] || '';
            if (sectionHtml) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionHtml, 'text/html');
              const renderedGrid = doc.querySelector('#product-grid');
              if (renderedGrid) {
                const productGrid = document.querySelector('#product-grid');
                if (productGrid) {
                  renderedGrid.querySelectorAll(':scope > .grid-item').forEach(li => {
                    productGrid.appendChild(li.cloneNode(true));
                  });
                  this._runInventoryWithLoader();
                }
                return;
              }
            }
          }
        } catch (err) {
          console.error('[CustomFilter] Section rendering failed on append:', err);
        }
      }

      // Fallback: append built cards
      const productGrid = document.querySelector('#product-grid');
      if (productGrid) {
        rawProducts.forEach(product => productGrid.appendChild(this._buildProductCard(product)));
        this._runInventoryWithLoader();
      }
    }

    _renderLoadMore(pagination) {
      document.querySelector('.cfd-load-more-wrap')?.remove();
      if (!pagination) return;

      const total   = pagination.total || 0;
      const limit   = pagination.limit || 20;
      const page    = pagination.page || this.currentPage || 1;
      const hasNext = pagination.hasNext != null ? pagination.hasNext : (page * limit < total);

      if (!hasNext) return;

      const gridContainer = document.querySelector('#ProductGridContainer');
      if (!gridContainer) return;

      const wrap = document.createElement('div');
      wrap.className = 'cfd-load-more-wrap load-more-container';
      wrap.style.cssText = 'text-align: center; margin: 2rem 0;';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cfd-load-more-btn load-more-button';
      btn.innerHTML = `Meer laden
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <line x1="7" y1="1" x2="7" y2="13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <line x1="1" y1="7" x2="13" y2="7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>`;
      btn.addEventListener('click', () => this._loadMore());

      wrap.appendChild(btn);
      gridContainer.after(wrap);
    }

    _setLoadMoreLoading(loading) {
      const btn = document.querySelector('.cfd-load-more-btn');
      if (!btn) return;
      btn.disabled = loading;
      btn.querySelector('svg')?.style.setProperty('opacity', loading ? '0' : '');
      const textNode = btn.childNodes[0];
      if (textNode) textNode.textContent = loading ? 'Laden… ' : 'Meer laden ';
    }

    // ── Loading state ─────────────────────────────────────────

    _setLoading(loading) {
      const overlay = document.querySelector('#ProductGridContainer .loading-overlay');
      if (overlay) overlay.classList.toggle('is-visible', loading);

      this._els('.js-cfd-show-results').forEach((btn) => {
        btn.disabled = loading;
        btn.classList.toggle('is-loading', loading);
      });
    }

    // ── Sync top filter bar pills ─────────────────────────────

    _syncFilterBarPills() {
      const totalActive =
        Object.values(this.selectedDynamic).reduce((n, s) => n + s.size, 0) +
        Object.values(this.selectedStatic).reduce((n, v) => {
          if (v instanceof Set) return n + v.size;
          if (v && v.type === 'price_range') return n + 1;
          return n;
        }, 0);

      const allFiltersBtn = document.querySelector('.js-open-custom-filter-drawer.custom-filter-pill-all-filters');
      if (allFiltersBtn) {
        allFiltersBtn.classList.toggle('is-active', totalActive > 0);
        const countEl = allFiltersBtn.querySelector('.cfd-pill-count');
        if (countEl) countEl.textContent = totalActive > 0 ? ` (${totalActive})` : '';
      }

      // Render individual active-filter pills in the top filter bar
      const barPillsEl = document.querySelector('.js-cfd-bar-active-pills');
      if (!barPillsEl) return;

      barPillsEl.innerHTML = '';

      Object.entries(this.selectedDynamic).forEach(([key, values]) => {
        const filter = this.dynamicFilters[key];
        values.forEach(val => {
          const def = filter?.values.find(v => v.value === val);
          const label = def?.label || val;
          barPillsEl.appendChild(this._createBarPill(label, () => {
            this._toggleDynamic(key, val, false);
            this._updateActivePills();
            this._updateGroupBadges();
            this._syncFilterBarPills();
            this._applyFilters();
          }));
        });
      });

      Object.entries(this.selectedStatic).forEach(([key, valuesOrObj]) => {
        if (valuesOrObj instanceof Set) {
          const filterDef = this.staticFiltersData.find(f => f.param_name === key);
          valuesOrObj.forEach(val => {
            const valDef = filterDef?.values?.find(v => v.value === val);
            const label = valDef?.label || val;
            barPillsEl.appendChild(this._createBarPill(label, () => {
              this._toggleStatic(key, val, false);
              this._updateActivePills();
              this._updateGroupBadges();
              this._syncFilterBarPills();
              this._applyFilters();
            }));
          });
        } else if (valuesOrObj && valuesOrObj.type === 'price_range') {
          const label = `€ ${valuesOrObj.min || '0'} – ${valuesOrObj.max || '∞'}`;
          barPillsEl.appendChild(this._createBarPill(label, () => {
            delete this.selectedStatic[key];
            this._updateActivePills();
            this._updateGroupBadges();
            this._syncFilterBarPills();
            this._applyFilters();
          }));
        }
      });
    }

    _createBarPill(label, onRemove) {
      const pill = document.createElement('span');
      pill.className = 'cfd-bar-pill';
      pill.innerHTML = `<span class="cfd-bar-pill-label">${this._esc(label)}</span>
        <button type="button" class="cfd-bar-pill-remove" aria-label="Filter verwijderen">&times;</button>`;
      pill.querySelector('.cfd-bar-pill-remove').addEventListener('click', (e) => {
        e.stopPropagation();
        onRemove();
      });
      return pill;
    }

    // ── Utility ───────────────────────────────────────────────

    _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    // Decode HTML entities (e.g. "&#39;" → "'", "&amp;" → "&"). Used to turn
    // Shopify's entity-encoded filter `value.value` back into the real value
    // for native facet URLs. A detached <textarea> decodes safely (no exec).
    _decodeEntities(str) {
      if (str == null) return str;
      if (!this._decoderEl) this._decoderEl = document.createElement('textarea');
      this._decoderEl.innerHTML = String(str);
      return this._decoderEl.value;
    }
  }

  customElements.define('custom-filter-drawer-root', CustomFilterDrawer);

  // ── Category format detection ────────────────────────────────────────────
  // Category identifiers start with a single letter (A–Z) and may be followed
  // by zero or more "-digits" groups:
  //   • Top-level category  → a single letter:  A, J
  //   • Nested categories   → letter + groups:  A-01, J-03-02, A-01-02-03
  // A single A–Z character (no digits) is therefore treated as a category
  // identifier; anything else is treated as a tag.
  function isCategoryFormat(id) {
    return /^[A-Za-z](-\d+)*$/.test(String(id));
  }

  // ── Standalone API helpers (mirrors the stockApi pattern in custom.js) ──

  async function customSearchFiltersApi(categoryIdentifiers, locale, tags = [], brands = [], selectedFilters = {}, price = null) {
    console.log('calling customSearchFiltersApi');
    console.log('categoryIdentifiers', categoryIdentifiers);
    const useCategory = categoryIdentifiers.length > 0 && isCategoryFormat(categoryIdentifiers[0]);
    console.log('useCategory format:', useCategory);
    try {
      const body = useCategory
        ? {
            categoryIdentifiers,
            ...(brands.length > 0 && { brands }),
            ...(tags.length > 0 && { tags }),
            selectedFilters,
            ...(price && { price }),
            locale,
          }
        : {
            tags: [...categoryIdentifiers, ...tags],
            ...(brands.length > 0 && { brands }),
            selectedFilters,
            ...(price && { price }),
            locale,
          };
      console.log('customSearchFiltersApi body', body);
      const response = await fetch('/apps/apis/api/custom-search/collection/filters', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) return;
      const data = await response.json();
      console.log('response data', data);
      return data;
    } catch (error) {
      return;
    }
  }

  async function customSearchProductsApi(categoryIdentifiers, tags, brands, filters, price, page, limit, sort) {
    console.log('calling customSearchProductsApi');
    console.log('customSearchProductsApi filters', filters);
    const useCategory = categoryIdentifiers.length > 0 && isCategoryFormat(categoryIdentifiers[0]);
    console.log('useCategory format:', useCategory);
    try {
      const body = useCategory
        ? { categoryIdentifiers: categoryIdentifiers, ...(tags.length > 0 && { tags: tags }), ...(brands.length > 0 && { brands: brands }), ...(sort && { sort: sort }), filters: filters, ...(price && { price: price }), page: page, limit: limit }
        : { tags: [...categoryIdentifiers, ...tags], ...(brands.length > 0 && { brands: brands }), ...(sort && { sort: sort }), filters: filters, ...(price && { price: price }), page: page, limit: limit };

        console.log('body', JSON.stringify(body))

      const response = await fetch('/apps/apis/api/custom-search/collection/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      console.log('response', response);
      if (!response.ok) return;
      const data = await response.json();
      console.log('filter data', data);
      return data;
    } catch (error) {
      return;
    }
  }

  // ── Search page API helpers ────────────────────────────────────────────

  async function searchFiltersApi(q, locale, brands = [], selectedFilters = {}, price = null, tags = []) {
    console.log('calling searchFiltersApi', { q, brands, selectedFilters, tags });
    const body = {
      q,
      ...(brands && brands.length > 0 && { brands }),
      ...(tags && tags.length > 0 && { tags }),
      selectedFilters,
      ...(price && { price }),
      locale,
    };
    console.log('searchFiltersApi body', body);
    try {
      const response = await fetch('/apps/apis/api/custom-search/search/filters', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) return;
      const data = await response.json();
      console.log('searchFiltersApi response', data);
      return data;
    } catch (error) {
      return;
    }
  }

  async function searchProductsApi(q, brands, filters, price, page, limit, sort, tags) {
    console.log('calling searchProductsApi', { q, brands, filters });
    try {
      const response = await fetch('/apps/apis/api/custom-search/search/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          q: q,
          ...(brands && brands.length > 0 && { brands: brands }),
          filters: filters,
          ...(price && { price: price }),
          ...(tags && tags.length > 0 && { tags }),
          page: page,
          limit: limit,
          ...(sort && { sort: sort }),
        }),
      });
      if (!response.ok) return;
      const data = await response.json();
      console.log('searchProductsApi response', data);
      return data;
    } catch (error) {
      return;
    }
  }

})();